import React, { FC, useState } from 'react';
import { useModal } from 'store/modal';
import { FaCheck } from 'react-icons/fa';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { useTranslation } from 'react-i18next';
import { signDocAsEmployer } from 'routes/client/documents/helpers';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import Spinner from 'components/Spinner';
import useInvalidateQueries from 'utils/helpers/useInvalidateQueries';

interface IProps {
  children?: any;
  id: string;
  docHolderId: string;
  buttonText: string;
  handlePreview: (docId: string) => void;
  invalidate: () => void;
  size?: 'sm' | 'lg';
  ariaLabel?: string;
}

const ApproveAndSignDoc: FC<IProps> = ({
  id,
  docHolderId,
  buttonText,
  handlePreview,
  invalidate,
  size = 'sm',
  ariaLabel,
}: IProps) => {
  const { t } = useTranslation();
  const { showModal, hideModal } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const invalidateQueries = useInvalidateQueries();

  // Approve and sign document
  const handleApproveAndSign = (docId: string) => {
    showModal({
      type: 'loading',
      heading: t('documents.eSign'),
      content: t('documents.eSignSubtitle'),
    });
    signDocAsEmployer(
      docId,
      docHolderId,
      showModal,
      hideModal,
      () => invalidateQueries([], invalidate),
      'documents.table.approvedAndSignedDocSuccessMsg',
      t
    );
  };

  // Confirm approval and sign
  const confirmApproveAndSign = (docId: string) => {
    showModal({
      type: 'confirm',
      heading: t('documents.table.confirmDocHeading'),
      content: (
        <>
          <p>{t('documents.table.confirmApproveAndSignDocMsg')}</p>
          <Button
            type="button"
            btnType="primary"
            outlined
            disabled={isLoading}
            onClick={() => handlePreviewDoc({ showModal, id: docId, setIsLoading })}
          >
            {!isLoading && t('documents.table.previewDoc')}
            {isLoading && <Spinner size={14} />}
          </Button>
          <hr />
        </>
      ),
      onConfirm: () => handleApproveAndSign(docId),
      confirmLabel: t('documents.table.confirmApproveAndSignDocLabel'),
    });
  };

  return (
    <div className="d-inline-block ml-2 mb-2 mb-md-0">
      <TooltipWrapper
        id="tooltip-approve-and-sign"
        overlay={buttonText}
      >
        <Button
          aria-label={ariaLabel}
          type="button"
          btnType="info"
          size={size}
          onClick={() => confirmApproveAndSign(id)}
        >
          <FaCheck size={14} />
        </Button>
      </TooltipWrapper>
    </div>
  );
};

export default ApproveAndSignDoc;
